"use client";

import { useEffect, useRef, useState } from "react";
import { useGetBulkMoveJobStatus } from "@/lib/api/generated";
import type { BulkMoveJobResponse } from "@/lib/api/model";

export const BULK_MOVE_TERMINAL_STATUSES = new Set([
  "completed",
  "completed_with_errors",
  "failed",
]);

/** Surface "still starting…" when the job stays queued this long. */
export const BULK_MOVE_STUCK_QUEUED_MS = 30_000;

/**
 * Soft client timeout — stop polling and warn; do **not** cancel server work.
 * Spec: ~10–15 min; plan: ~12 min.
 */
export const BULK_MOVE_SOFT_TIMEOUT_MS = 12 * 60 * 1000;

const POLL_INITIAL_MS = 1_000;
const POLL_MID_MS = 3_000;
const POLL_MAX_MS = 5_000;

export function isBulkMoveTerminalStatus(status: string | undefined): boolean {
  return !!status && BULK_MOVE_TERMINAL_STATUSES.has(status);
}

/** ~1s → backoff to 3–5s while the job is still in flight. */
export function bulkMovePollIntervalMs(elapsedMs: number): number {
  if (elapsedMs < 4_000) return POLL_INITIAL_MS;
  if (elapsedMs < 12_000) return POLL_MID_MS;
  return POLL_MAX_MS;
}

export type UseBulkMoveJobPollResult = {
  data: BulkMoveJobResponse | undefined;
  isTerminal: boolean;
  stuckStarting: boolean;
  timedOut: boolean;
  isPolling: boolean;
};

/**
 * Polls GET bulk-move job status while `queued|running`, stops on terminal
 * statuses, surfaces stuck-starting and soft-timeout flags.
 */
export function useBulkMoveJobPoll({
  jobId,
  bulkMoveJobId,
  enabled = true,
}: {
  jobId: string;
  bulkMoveJobId: string | null;
  enabled?: boolean;
}): UseBulkMoveJobPollResult {
  const active = enabled && !!jobId && !!bulkMoveJobId;
  const startedAtRef = useRef(Date.now());
  const [stuckStarting, setStuckStarting] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!active) {
      setStuckStarting(false);
      setTimedOut(false);
      return;
    }
    startedAtRef.current = Date.now();
    setStuckStarting(false);
    setTimedOut(false);

    const stuckTimer = setTimeout(() => {
      setStuckStarting(true);
    }, BULK_MOVE_STUCK_QUEUED_MS);

    const softTimer = setTimeout(() => {
      setTimedOut(true);
    }, BULK_MOVE_SOFT_TIMEOUT_MS);

    return () => {
      clearTimeout(stuckTimer);
      clearTimeout(softTimer);
    };
  }, [active, jobId, bulkMoveJobId]);

  const query = useGetBulkMoveJobStatus(jobId, bulkMoveJobId ?? "", {
    query: {
      enabled: active && !timedOut,
      staleTime: 0,
      retry: false,
      refetchInterval: (q) => {
        if (timedOut) return false;
        const status = q.state.data?.status;
        if (isBulkMoveTerminalStatus(status)) return false;
        const elapsed = Date.now() - startedAtRef.current;
        if (elapsed >= BULK_MOVE_SOFT_TIMEOUT_MS) return false;
        return bulkMovePollIntervalMs(elapsed);
      },
    },
  });

  const status = query.data?.status;
  const isTerminal = isBulkMoveTerminalStatus(status);

  // Once we leave queued, clear stuckStarting even if the 30s timer already fired.
  const effectiveStuckStarting =
    stuckStarting && !isTerminal && status === "queued";

  // Soft timeout only applies while still in-flight (and never after a terminal).
  const effectiveTimedOut = timedOut && !isTerminal;

  return {
    data: query.data,
    isTerminal,
    stuckStarting: effectiveStuckStarting,
    timedOut: effectiveTimedOut,
    isPolling: active && !isTerminal && !effectiveTimedOut,
  };
}
